/**
 * Whether a repair order's equipment type is Chassis — the branch that decides
 * which column holds the unit number, which extra fields the drawer shows, and
 * which column the drawer's identifier input writes to.
 *
 * Case-insensitive: the drawer writes 'Chassis', but every V1-era row stores
 * 'CHASSIS' (~70k of them). A case-sensitive check classes all of those as
 * non-chassis, which binds the drawer's identifier input to `container` — the
 * mounted shipping container — instead of the chassis.
 */
export function isChassisRepairPart(repairpart: string | null | undefined): boolean {
  return (repairpart ?? '').trim().toLowerCase() === 'chassis';
}

/**
 * Resolve the equipment/unit number for a repair order.
 *
 * `Chassis` orders keep the unit in `chassisid` — their `container` is the box
 * riding on the chassis, not the unit. Every other equipment type (Van, Truck,
 * Container, Mule, Golf Cart, Other, and the legacy `EQUIPMENT` bucket) keeps
 * the unit in `container`, which is also what the drawer's Equipment # input
 * writes to.
 *
 * Two details that look like edge cases but cover most of the table:
 *
 *  - `repairpart` is matched case-INSENSITIVELY. The drawer writes 'Chassis',
 *    but every V1-era row stores 'CHASSIS' (~70k of them), and a
 *    case-sensitive check sends all of those to `container` — the mounted
 *    shipping container instead of the chassis.
 *  - The `container -> chassisid` fallback covers rows created by the V2
 *    repair-order system, which writes the unit to `chassisid` whatever the
 *    equipment type. Without it ~2.5k live ROs across ~1.1k units resolve to
 *    nothing, and most of those units have no other RO to recover them from.
 *
 * This is the single source of truth for "which column holds this order's unit
 * number". The Repair Orders grid (`equipmentNumberFor`) delegates here, and
 * the backend's `_equipment_unit_for` / `_unit_expr_sql` mirror it so the ROs
 * badge, the by-equipment sidebar and the GPS lookup all agree on one unit.
 *
 * Returns the trimmed identifier, or '' when neither column is set.
 */
export function resolveRepairUnitNumber(
  repairpart: string | null | undefined,
  chassisid: string | null | undefined,
  container: string | null | undefined
): string {
  const chassis = (chassisid ?? '').trim();
  if (isChassisRepairPart(repairpart)) return chassis;
  return (container ?? '').trim() || chassis;
}

export interface RepairPartChangePatch {
  repairpart: string;
  chassisid?: string;
  chassispool?: string;
  withequipment?: string;
}

/**
 * The form patch that applies an equipment-type change on a repair order.
 *
 * The drawer only shows Chassis ID, Chassis Pool and Is Mounted? while the
 * type is Chassis, but hiding a field does not unset it — the save wrote the
 * stale values anyway. That is how live orders ended up as a Van carrying
 * chassisid='VAN': the type was changed after the fact and the old chassis
 * value stayed behind, silently grouping every such order under a bogus "VAN"
 * unit. Leaving Chassis therefore clears all three.
 *
 * `container` is deliberately left alone. It is the Equipment # for every
 * other type, and on a Chassis -> Container switch the container number the
 * user already typed is exactly the right value to keep.
 *
 * Only crossing the Chassis boundary clears anything: Van -> Truck keeps the
 * unit, which lives in the same column for both.
 */
export function repairPartChangePatch(
  prevPart: string | null | undefined,
  nextPart: string
): RepairPartChangePatch {
  if (isChassisRepairPart(prevPart) && !isChassisRepairPart(nextPart)) {
    return { repairpart: nextPart, chassisid: '', chassispool: '', withequipment: 'N' };
  }
  return { repairpart: nextPart };
}

export interface BilledToOption {
  recno: number;
  name: string;
}

export interface BilledToSources {
  drivers: { recno: number; unitnumber: string; firstname: string; lastname: string }[];
  customerLocations: { recno: number; name: string }[];
  gwiLocations: { recno: number; name: string }[];
}

/**
 * Build the option list for a repair order's "Billed To" entity picker.
 *
 * The options come from a different lookup per type: Driver -> drivers,
 * Customer -> customer locations, Terminal -> GWI locations. An unrecognised or
 * empty type yields no options (the picker is disabled in that state).
 *
 * Drivers render as "unit — First Last" to match how they're identified
 * elsewhere on the repair order form; locations render by name. Options are
 * sorted by the rendered label.
 *
 * When `savedRecno` is set but absent from the lookup — a deactivated driver, a
 * retired location — the stored `savedName` is prepended as an "(inactive)"
 * option so the picker still shows what the order was billed to instead of
 * silently rendering blank. A `savedRecno` with no `savedName` is not
 * recoverable and is left out.
 */
export function buildBilledToOptions(
  billedToType: string,
  sources: BilledToSources,
  savedRecno?: number | null,
  savedName?: string | null
): BilledToOption[] {
  let opts: BilledToOption[];
  switch (billedToType) {
    case 'Driver':
      opts = sources.drivers.map((d) => ({
        recno: d.recno,
        name: [d.unitnumber, [d.firstname, d.lastname].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(' — '),
      }));
      break;
    case 'Customer':
      opts = sources.customerLocations.map((l) => ({ recno: l.recno, name: l.name }));
      break;
    case 'Terminal':
      opts = sources.gwiLocations.map((l) => ({ recno: l.recno, name: l.name }));
      break;
    default:
      opts = [];
  }

  opts = [...opts].sort((a, b) => a.name.localeCompare(b.name));

  if (savedRecno && savedName && !opts.some((o) => o.recno === savedRecno)) {
    return [{ recno: savedRecno, name: `${savedName} (inactive)` }, ...opts];
  }
  return opts;
}

/**
 * Pick a repair order's region from its two candidate sources, preferring the
 * equipment's home location over the reporting driver's region.
 *
 * A blank / null / whitespace-only value from either source is a MISS: the
 * equipment region wins only when non-empty, otherwise we fall through to the
 * driver region, and finally to '' when neither source hits.
 *
 * Returns the trimmed winning region, or '' when neither hits.
 */
export function pickRepairRegion(
  equipmentRegion: string | null | undefined,
  driverRegion: string | null | undefined
): string {
  const equip = (equipmentRegion ?? '').trim();
  if (equip) return equip;
  const driver = (driverRegion ?? '').trim();
  if (driver) return driver;
  return '';
}
